import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/site";
import { fadeUp } from "../components/sections";

const TOKEN_KEY = "esh_admin_token";

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // If already logged in, bounce to dashboard
  useEffect(() => {
    const token = getAdminToken();
    if (!token) return;
    apiClient
      .get("/admin/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(() => navigate("/admin/dashboard", { replace: true }))
      .catch(() => setAdminToken(null));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/admin/login", { email, password });
      setAdminToken(data.access_token);
      toast.success(`Welcome, ${data.name}.`);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-[calc(100vh-80px)] flex items-center px-6 md:px-12 py-24">
      <motion.div
        {...fadeUp}
        className="w-full max-w-md mx-auto border border-[#27272A] bg-[#0A0A0C] p-10 md:p-12"
      >
        <div className="w-12 h-12 border border-[#C2533B] flex items-center justify-center mb-8">
          <Lock size={18} strokeWidth={1.5} style={{ color: "#C2533B" }} />
        </div>
        <p className="overline mb-3">Admin Access</p>
        <h1 className="font-serif-display text-3xl md:text-4xl text-white font-light leading-tight mb-2">
          Sign in to the dashboard.
        </h1>
        <p className="text-zinc-500 text-sm mb-10 leading-relaxed">
          Restricted to authorised Elite Solutions Hub partners.
        </p>

        <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="overline mb-3 block" htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              className="input-line"
              placeholder="you@elitesolutionshub.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              required
            />
          </div>
          <div>
            <label className="overline mb-3 block" htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              className="input-line"
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {submitting ? "Signing in…" : "Sign In"} <ArrowRight size={14} strokeWidth={1.8} />
          </button>
        </form>

        <p className="text-zinc-600 text-xs mt-8 text-center">
          Forgot your password? Contact David directly.
        </p>
        <p className="text-center mt-6">
          <Link to="/" className="text-zinc-500 text-xs hover:text-white transition">
            ← Back to site
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
